import { jsx, jsxs } from 'react/jsx-runtime';
import React, { forwardRef, useContext, Children, cloneElement, useRef, useState, useMemo, useEffect, useCallback, isValidElement } from 'react';
import { flip, shift, size, useFloating, autoUpdate } from '@floating-ui/react-dom';
import { Fade } from '@wds/motion';
import { safeRest, getDisplayName, defaultFonts, uniqueId, notEmpty, KEYS, conditionGuard, isElement, handlePortal } from '@wds/utils';
import { List } from '@wds/list';
import { createMemoStyles } from '@wds/styles';
import { IconArrowLeft } from '@wds/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var MenuContext = /*#__PURE__*/React.createContext({
  nestedActiveId: null,
  setNestedActiveId: function () {},
  nestedActiveRef: null,
  handleMenuClose: function () {}
});

var MenuItem = /*#__PURE__*/forwardRef(function (_a, ref) {
  var addOn = _a.addOn,
    _b = _a.addOnPlacement,
    addOnPlacement = _b === void 0 ? 'left' : _b,
    _c = _a.addOnIsInteractive,
    addOnIsInteractive = _c === void 0 ? false : _c,
    _d = _a.autoClose,
    autoClose = _d === void 0 ? true : _d,
    color = _a.color,
    onClick = _a.onClick,
    children = _a.children,
    _e = _a.disabled,
    disabled = _e === void 0 ? false : _e,
    component = _a.component,
    _f = _a.isNavigable,
    isNavigable = _f === void 0 ? false : _f;
    _a.isFocused;
    var _h = _a.marked,
    marked = _h === void 0 ? false : _h,
    _j = _a.upgrade,
    upgrade = _j === void 0 ? false : _j,
    groupindex = _a.groupindex,
    index = _a.index,
    _k = _a.role,
    role = _k === void 0 ? 'menuitem' : _k,
    id = _a.id,
    nestedId = _a.nestedId,
    rest = __rest(_a, ["addOn", "addOnPlacement", "addOnIsInteractive", "autoClose", "color", "onClick", "children", "disabled", "component", "isNavigable", "isFocused", "marked", "upgrade", "groupindex", "index", "role", "id", "nestedId"]);
  var _l = useContext(MenuContext),
    nestedActiveId = _l.nestedActiveId,
    setNestedActiveId = _l.setNestedActiveId,
    handleMenuClose = _l.handleMenuClose;
  var onHandleClick = function onHandleClick(e) {
    if (onClick) onClick(e);
    if (nestedId && nestedId !== nestedActiveId) {
      setNestedActiveId(nestedId);
    } else if (autoClose && handleMenuClose) {
      handleMenuClose(e);
    }
    e.stopPropagation();
  };
  return jsx(List.Item, __assign({
    addOnLeft: addOnPlacement === 'left' ? addOn : undefined,
    addOnLeftIsInteractive: addOnPlacement === 'left' && addOnIsInteractive,
    addOnRight: addOnPlacement === 'right' ? addOn : undefined,
    addOnRightIsInteractive: addOnPlacement === 'right' && addOnIsInteractive,
    color: color,
    component: component,
    "data-group": groupindex,
    disabled: disabled,
    index: index,
    isNavigable: isNavigable,
    marked: marked,
    menu: true,
    interactive: true,
    onClick: onHandleClick,
    role: role,
    upgrade: upgrade,
    tabIndex: disabled ? -1 : 0,
    ref: ref,
    id: id,
    "aria-owns": nestedId
  }, safeRest(rest), {
    children: children
  }));
});
MenuItem.displayName = 'MenuItem';

var useStyles$5 = createMemoStyles(function (theme) {
  var border = theme.border;
  return {
    wrenchMenuGroup: function () {
      return {
        borderBottom: border.main,
        '&:last-of-type': {
          borderBottom: 0
        }
      };
    }
  };
});
var renderListItems = function renderListItems(_a) {
  var children = _a.children,
    groupIndex = _a.groupIndex,
    onKeyDown = _a.onKeyDown;
  var listItems = Children.map(children, function (child) {
    if (! /*#__PURE__*/React.isValidElement(child)) return null;
    return /*#__PURE__*/cloneElement(child, {
      groupindex: groupIndex,
      onKeyDown: onKeyDown
    });
  });
  return listItems;
};
var MenuGroup = function MenuGroup(_a) {
  var children = _a.children,
    title = _a.title,
    dense = _a.dense,
    groupIndex = _a.groupIndex,
    onKeyDown = _a.onKeyDown,
    rest = __rest(_a, ["children", "title", "dense", "groupIndex", "onKeyDown"]);
  var wrenchMenuGroup = useStyles$5().wrenchMenuGroup;
  return jsx("section", {
    className: wrenchMenuGroup,
    children: jsxs(List, __assign({
      noRules: true,
      dense: dense,
      interactive: true
    }, rest, {
      children: [title && jsx(List.Item, {
        children: title
      }), renderListItems({
        children: children,
        groupIndex: groupIndex,
        onKeyDown: onKeyDown
      })]
    }))
  });
};
MenuGroup.displayName = 'MenuGroup';

var useStyles$4 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  return {
    wrenchMenuFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: [spacing[4], spacing[5]],
      background: palette.background.light,
      borderTop: "1px solid ".concat(palette.border.muted),
      color: palette.text.dark
    },
    wrenchMenuActions: {
      display: 'flex',
      flexGrow: 1,
      flexWrap: 'wrap',
      justifyContent: function (_a) {
        var align = _a.align;
        return align === 'left' ? 'flex-start' : 'flex-end';
      },
      '&:not([class^="wrenchButtonGroup"])': {
        '& > button, & > a': {
          marginLeft: spacing[4]
        }
      }
    }
  };
});

var MenuFooter = function MenuFooter(_a) {
  var _b = _a.align,
    align = _b === void 0 ? 'left' : _b,
    children = _a.children;
  var _c = useStyles$4({
      align: align
    }),
    wrenchMenuActions = _c.wrenchMenuActions,
    wrenchMenuFooter = _c.wrenchMenuFooter;
  return jsx("footer", {
    className: wrenchMenuFooter,
    children: jsx("div", {
      className: wrenchMenuActions,
      children: children
    })
  });
};

var useStyles$3 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchMenuBody: {
      overflowY: 'auto',
      flexGrow: 1,
      padding: function (_a) {
        var padding = _a.padding;
        return padding ? [[spacing[4], spacing[5], spacing[5], spacing[5]]] : undefined;
      }
    }
  };
});

var MenuBody = function MenuBody(_a) {
  var children = _a.children,
    _b = _a.padding,
    padding = _b === void 0 ? false : _b,
    rest = __rest(_a, ["children", "padding"]);
  var wrenchMenuBody = useStyles$3({
    padding: padding
  }).wrenchMenuBody;
  var renderItemContainer = function renderItemContainer() {
    var _a;
    var items = (_a = Children.map(children, function (child) {
      if (getDisplayName(child) === 'MenuItem') {
        return child;
      }
      return null;
    })) !== null && _a !== void 0 ? _a : [];
    // all children are MenuItems, so render as a list
    if (items.length === Children.count(children)) {
      return jsx(List, __assign({
        interactive: true
      }, rest, {
        children: children
      }));
    }
    return children;
  };
  return jsx("section", {
    className: wrenchMenuBody,
    children: renderItemContainer()
  });
};
MenuBody.displayName = 'MenuBody';

var useStyles$2 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize;
  return {
    wrenchMenuHeader: function () {
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: [spacing[5], spacing[5], spacing[4], spacing[5]],
        background: palette.background.light,
        flexShrink: 0,
        flexDirection: 'column'
      };
    },
    wrenchMenuText: {
      fontSize: 20,
      fontWeight: fontWeight.medium,
      color: palette.text.dark,
      marginTop: 0,
      marginBottom: spacing[2]
    },
    wrenchMenuTitle: {
      flexGrow: 1,
      width: '100%'
    },
    wrenchMenuSubText: {
      fontSize: fontSize.bodySm,
      fontWeight: fontWeight.regular,
      color: palette.text.darkMuted,
      margin: [spacing[1], 0, 0]
    },
    wrenchMenuAddOn: {
      flexGrow: 1,
      marginTop: spacing[3],
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    }
  };
});

var MenuHeader = function MenuHeader(_a) {
  var addOn = _a.addOn,
    header = _a.header,
    id = _a.id,
    subHeader = _a.subHeader;
  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }
  var _b = useStyles$2(),
    wrenchMenuAddOn = _b.wrenchMenuAddOn,
    wrenchMenuHeader = _b.wrenchMenuHeader,
    wrenchMenuSubText = _b.wrenchMenuSubText,
    wrenchMenuText = _b.wrenchMenuText,
    wrenchMenuTitle = _b.wrenchMenuTitle;
  return jsxs("section", {
    className: wrenchMenuHeader,
    children: [jsxs("div", {
      className: wrenchMenuTitle,
      children: [header && jsx("h2", {
        className: wrenchMenuText,
        id: id,
        children: header
      }), subHeader && jsx("p", {
        className: wrenchMenuSubText,
        children: subHeader
      })]
    }), addOn && jsx("div", {
      className: wrenchMenuAddOn,
      children: addOn
    })]
  });
};

var useStyles$1 = createMemoStyles(function () {
  return {
    wrenchMenuNested: {
      display: function (_a) {
        var active = _a.active;
        return active ? null : 'none';
      }
    }
  };
});
var MenuNested = function MenuNested(_a) {
  var children = _a.children,
    id = _a.id,
    parentId = _a.parentId,
    title = _a.title,
    rest = __rest(_a, ["children", "id", "parentId", "title"]);
  var _b = useContext(MenuContext),
    nestedActiveId = _b.nestedActiveId,
    setNestedActiveId = _b.setNestedActiveId,
    nestedActiveRef = _b.nestedActiveRef;
  var active = id === nestedActiveId;
  var wrenchMenuNested = useStyles$1({
    active: active
  }).wrenchMenuNested;
  return jsx("div", {
    ref: active ? nestedActiveRef : undefined,
    className: wrenchMenuNested,
    children: jsxs(MenuGroup, __assign({}, rest, {
      id: id,
      children: [title && jsxs(MenuItem, {
        autoClose: false,
        onClick: function () {
          setNestedActiveId(parentId);
        },
        children: [jsx(IconArrowLeft, {
          title: title
        }), " ", title]
      }), children]
    }))
  });
};
MenuNested.displayName = 'MenuNested';

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    shadow = theme.shadow;
  var menu = {
    boxShadow: shadow.space,
    maxWidth: 300,
    minWidth: 175,
    maxHeight: 325
  };
  return {
    wrenchMenuContainer: __assign(__assign({
      boxSizing: 'border-box'
    }, defaultFonts(theme)), {
      position: 'relative',
      display: function (_a) {
        var stretched = _a.stretched;
        return stretched ? 'block' : 'inline-block';
      },
      outline: 'none'
    }),
    wrenchMenu: function (_a) {
      var menuStyle = _a.menuStyle,
        stretched = _a.stretched,
        elevation = _a.elevation;
      return __assign({
        zIndex: elevation !== null && elevation !== void 0 ? elevation : theme.elevation.sky,
        display: 'block',
        outline: 'none',
        textAlign: 'left',
        color: palette.text.dark,
        minWidth: menu.minWidth,
        maxWidth: stretched ? 'none' : menu.maxWidth,
        boxShadow: menu.boxShadow,
        whiteSpace: 'noWrap'
      }, menuStyle);
    },
    wrenchMenuBody: {
      background: palette.background.light,
      maxHeight: function (_a) {
        var noMaxHeight = _a.noMaxHeight;
        return noMaxHeight ? '100vh' : menu.maxHeight;
      },
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }
  };
});

var FOCUS_STATES;
(function (FOCUS_STATES) {
  FOCUS_STATES["TOP"] = "top";
  FOCUS_STATES["BOTTOM"] = "bottom";
})(FOCUS_STATES || (FOCUS_STATES = {}));
var MENU_PLACEMENTS = ['bottom-right', 'bottom-center', 'bottom-left', 'top-right', 'top-center', 'top-left'];

// Maps the placement prop's options to floating-ui's options.
var placementMap = {
  'bottom-left': 'bottom-start',
  'bottom-center': 'bottom',
  'bottom-right': 'bottom-end',
  'top-left': 'top-start',
  'top-center': 'top',
  'top-right': 'top-end'
};
var matchWidth = function matchWidth(_a) {
  var rects = _a.rects,
    elements = _a.elements;
  elements.floating.style.minWidth = "".concat(rects.reference.width, "px");
};
var Menu = function Menu(_a) {
  var id = _a.id,
    _b = _a.autoClose,
    autoClose = _b === void 0 ? true : _b,
    _c = _a.closeOnBlur,
    closeOnBlur = _c === void 0 ? true : _c,
    children = _a.children,
    _d = _a.dense,
    dense = _d === void 0 ? false : _d,
    _e = _a.placement,
    placement = _e === void 0 ? 'bottom-left' : _e,
    menuTrigger = _a.menuTrigger,
    _f = _a.noMaxHeight,
    noMaxHeight = _f === void 0 ? false : _f,
    onChange = _a.onChange,
    _g = _a.show,
    showProp = _g === void 0 ? false : _g,
    _h = _a.stretched,
    stretched = _h === void 0 ? false : _h,
    renderTo = _a.renderTo,
    _j = _a.role,
    role = _j === void 0 ? 'menu' : _j,
    _k = _a.noRules,
    noRules = _k === void 0 ? false : _k,
    elevation = _a.elevation,
    _l = _a.preventDefault,
    preventDefault = _l === void 0 ? false : _l,
    _m = _a.stopPropagation,
    stopPropagation = _m === void 0 ? false : _m,
    _o = _a.variant,
    variant = _o === void 0 ? 'list' : _o,
    _p = _a.alwaysRenderContent,
    alwaysRenderContent = _p === void 0 ? false : _p,
    anchorRef = _a.anchorRef,
    _q = _a.matchTriggerWidth,
    matchTriggerWidth = _q === void 0 ? false : _q,
    defaultNestedId = _a.defaultNestedId,
    rest = __rest(_a, ["id", "autoClose", "closeOnBlur", "children", "dense", "placement", "menuTrigger", "noMaxHeight", "onChange", "show", "stretched", "renderTo", "role", "noRules", "elevation", "preventDefault", "stopPropagation", "variant", "alwaysRenderContent", "anchorRef", "matchTriggerWidth", "defaultNestedId"]);
  var floatingUiMiddleware = [flip({
    crossAxis: false
  }), shift()];
  if (matchTriggerWidth) {
    floatingUiMiddleware.push(size({
      apply: matchWidth
    }));
  }
  var _r = useFloating({
      placement: placementMap[placement],
      middleware: floatingUiMiddleware,
      elements: {
        reference: anchorRef || undefined
      }
    }),
    floatingRefs = _r.refs,
    floatingElements = _r.elements,
    floatingStyles = _r.floatingStyles,
    floatingUpdate = _r.update;
  // Refs
  var menuRef = useRef(null);
  var innerMenuRef = useRef(null);
  var itemsRef = useRef([]);
  var nestedActiveRef = useRef(null);
  var autoCloseTimeoutRef = useRef();
  var _s = useState(defaultNestedId),
    nestedActiveId = _s[0],
    setNestedActiveId = _s[1];
  // Menu State Items
  var _t = useState(showProp),
    showState = _t[0],
    setShowState = _t[1];
  var _u = useState(null),
    focusState = _u[0],
    setFocusState = _u[1];
  var _v = useState(-1),
    focusIndex = _v[0],
    setFocusIndex = _v[1];
  // For memoizing handleOnKeyDown
  var keyPropRef = useRef({
    focusIndex: focusIndex,
    showState: showState
  });
  keyPropRef.current = {
    focusIndex: focusIndex,
    showState: showState
  };
  // aria-owns for a11y
  var menuID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('wds-menu-');
  }, [id]);
  // Styles
  var classes = useStyles({
    menuStyle: {},
    noMaxHeight: noMaxHeight,
    placement: placement,
    stretched: stretched,
    elevation: elevation
  });
  /**
   * a way to help replicate componentWillReceiveProps
   * and componentDidUpdate
   */
  var prevPropsRef = useRef({});
  useEffect(function () {
    var _a;
    prevPropsRef.current = {
      showProp: showProp,
      showState: showState
    };
    /**
     * Each time the MenuItem List is generated through the menu button trigger
     * this code will create a list of accessible ListItems to traverse through.
     */
    if (showState && innerMenuRef.current) {
      var parentRef = nestedActiveId ? nestedActiveRef : innerMenuRef;
      itemsRef.current = [].slice.call((_a = parentRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByTagName('li')).map(function (item) {
        return item.firstChild;
      }).filter(notEmpty);
    }
    /**
     * When showProp is still set to True, but showState is false,
     * we need sync showState up with showProp
     */
    if (showProp && showState !== showProp) {
      setShowState(showProp);
    }
  }, [showProp, showState, children, nestedActiveId]);
  // This will auto focus the first or last item
  useEffect(function () {
    var _a;
    if (focusState) {
      var focusInd = focusState === FOCUS_STATES.TOP ? 0 : itemsRef.current.length - 1;
      if (itemsRef.current[focusInd]) {
        (_a = itemsRef.current[focusInd]) === null || _a === void 0 ? void 0 : _a.focus();
      }
      setFocusIndex(focusInd);
    }
  }, [focusState, nestedActiveId]);
  // When the alwaysRenderContent prop is true, floating-ui is instantiated
  // on elements that persist in the DOM across state changes. To avoid memory
  // leaks, we must destroy the autoUpdate() reference when the menu is hidden
  // but still in the DOM.
  //
  // floating-ui handles this automatically for conditionally rendered content,
  // but we have to support this existing use case for Menu.
  //
  // More info: https://floating-ui.com/docs/autoupdate
  useEffect(function () {
    var reference = floatingElements.reference,
      floating = floatingElements.floating;
    if (showState && reference && floating) {
      var cleanup = autoUpdate(reference, floating, floatingUpdate);
      return cleanup;
    }
  }, [showState, floatingElements, floatingUpdate]);
  // componentWillReceiveProps
  useEffect(function () {
    setShowState(showProp);
  }, [showProp]);
  var closeMenu = useCallback(function () {
    setFocusState(null);
    setFocusIndex(-1);
    setNestedActiveId(defaultNestedId || undefined);
  }, [defaultNestedId]);
  var menuChange = useCallback(function (showMenu) {
    setShowState(showMenu);
    if (onChange) {
      onChange(showMenu);
    }
  }, [onChange]);
  var handleOnClick = function handleOnClick(e) {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (preventDefault) {
      e.preventDefault();
    }
    menuChange(!showState);
  };
  var handleKeyScroll = function handleKeyScroll(index) {
    if (itemsRef.current) {
      if (index > -1 && itemsRef.current[index]) {
        var menuItemHeight = itemsRef.current[index].offsetTop + itemsRef.current[index].offsetHeight;
        var menuContainerHeight = innerMenuRef.current.offsetHeight;
        innerMenuRef.current.scrollTop = menuItemHeight <= menuContainerHeight ? 0 : menuItemHeight - menuContainerHeight;
      }
    }
  };
  var handleOnKeyDown = useCallback(function (e) {
    var key = e.key;
    if (keyPropRef.current.showState) {
      var itemsLen = itemsRef.current.length - 1;
      var focusInd = keyPropRef.current.focusIndex === -1 ? 0 : keyPropRef.current.focusIndex;
      var newFocusIndex = 0;
      switch (true) {
        case key === KEYS.TAB && variant === 'list':
        case key === KEYS.ESC:
          menuChange(false);
          if (menuRef.current.firstChild) {
            menuRef.current.firstChild.focus();
          }
          e.preventDefault();
          break;
        /**
         * TODO: Needs to handle space in a better way
         */
        case key === KEYS.ENTER:
          itemsRef.current[focusInd].click();
          break;
        case key === KEYS.UP:
          newFocusIndex = focusInd > 0 ? focusInd - 1 : itemsLen;
          break;
        case key === KEYS.DOWN:
          newFocusIndex = focusInd === itemsLen ? 0 : focusInd + 1;
          break;
      }
      if (key === KEYS.UP || key === KEYS.DOWN) {
        setFocusIndex(newFocusIndex);
        handleKeyScroll(newFocusIndex);
        if (newFocusIndex > -1 && itemsRef.current[newFocusIndex]) {
          itemsRef.current[newFocusIndex].focus();
          e.preventDefault();
        }
      }
    }
  }, [menuChange, variant]);
  var handleOnTriggerKeyDown = function handleOnTriggerKeyDown(e) {
    var key = e.key;
    if (/*#__PURE__*/React.isValidElement(menuTrigger) && conditionGuard(menuTrigger.props, Object.prototype.hasOwnProperty.call(menuTrigger.props, 'onKeyDown'))) {
      menuTrigger.props.onKeyDown(e);
    }
    var isNotInput = getDisplayName(menuTrigger) !== 'Input';
    if (isNotInput) {
      switch (key) {
        case KEYS.ENTER:
        case KEYS.SPACE:
        case KEYS.DOWN:
          // TODO: Find a better way to handle this type
          handleOnClick(e);
          setFocusState(FOCUS_STATES.TOP);
          e.preventDefault();
          break;
        case KEYS.UP:
          // TODO: Find a better way to handle this type
          handleOnClick(e);
          setFocusState(FOCUS_STATES.BOTTOM);
          e.preventDefault();
          break;
      }
    }
  };
  var handleAutoCloseBlur = function handleAutoCloseBlur(e) {
    autoCloseTimeoutRef.current = window.setTimeout(function () {
      var _a, _b, _c;
      // if the menu is already closed, ignore this blur event
      if (!showState) {
        return;
      }
      var renderToComp = null;
      if (renderTo) {
        if (conditionGuard(renderTo, isElement(renderTo))) renderToComp = renderTo;else if (renderTo.current) {
          renderToComp = renderTo.current;
        }
      }
      var isFocusInRenderContainer = renderToComp === null || renderToComp === void 0 ? void 0 : renderToComp.contains(document.activeElement);
      var isFocusInMenuRef = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement);
      // When using a nested menu, changing the visibility of a given nesting
      // group causes a re-render. Menu and its sub-components don't seem to
      // persist underlying DOM nodes on re-render.
      //
      // This means when a nested item is selected, its element gets replaced
      // with a new one and the activeElement reference is no longer "contained"
      // by the Menu. This causes the above checks to fail and triggers another
      // blur event (blur gets triggered when an element becomes hidden).
      //
      // This additional check looks at the stored reference to the original
      // (now orphaned) element to see if it's a menu item, since a blur event
      // should never trigger normally from focusing a child element.
      var isTargetMenuItem = ((_b = e.target) === null || _b === void 0 ? void 0 : _b.getAttribute('role')) === 'menuitem';
      // After clicking a nested menu trigger, the panel containing the trigger
      // gets hidden and loses focus. This resets focus to the parent container.
      if (isTargetMenuItem && !isFocusInMenuRef) {
        (_c = menuRef.current) === null || _c === void 0 ? void 0 : _c.focus();
      }
      if (!isFocusInRenderContainer && !isFocusInMenuRef && !isTargetMenuItem) {
        menuChange(false);
      }
    }, 0);
  };
  // clear the timeout when the component unmounts
  useEffect(function () {
    return function () {
      window.clearTimeout(autoCloseTimeoutRef.current);
    };
  }, []);
  var handleMenuClose = useCallback(function () {
    if (autoClose) {
      menuChange(false);
    }
  }, [autoClose, menuChange]);
  var renderListItems = function renderListItems(ch, groupindex) {
    var listItems = Children.map(ch, function (child) {
      if (! /*#__PURE__*/React.isValidElement(child)) return null;
      return /*#__PURE__*/cloneElement(child, {
        groupindex: groupindex,
        onKeyDown: handleOnKeyDown
      });
    });
    return listItems;
  };
  var renderItems = function renderItems(ch) {
    var listItems = Children.map(ch, function (child) {
      if (! /*#__PURE__*/React.isValidElement(child)) return null;
      if (getDisplayName(child) === 'MenuBody') {
        return /*#__PURE__*/cloneElement(child, {
          dense: dense,
          noRules: noRules
        });
      }
      if (['MenuHeader', 'MenuFooter'].includes(getDisplayName(child))) {
        return child;
      }
      return null;
    });
    return listItems;
  };
  var renderMenuGroup = function renderMenuGroup(el, groupIndex) {
    return /*#__PURE__*/cloneElement(el, {
      groupIndex: groupIndex,
      dense: dense,
      onKeyDown: handleOnKeyDown
    });
  };
  var renderItemContainer = function renderItemContainer() {
    var _a;
    var items = (_a = Children.map(children, function (child, index) {
      if (! /*#__PURE__*/isValidElement(child)) {
        return undefined;
      }
      if (getDisplayName(child) !== 'MenuGroup') {
        return undefined;
      }
      return renderMenuGroup(child, index);
    })) !== null && _a !== void 0 ? _a : [];
    // children are all Menu.Group
    if (items.length) {
      return items;
    }
    if (variant === 'list') {
      return jsx(List, __assign({
        interactive: true,
        dense: dense,
        noRules: noRules
      }, rest, {
        children: renderListItems(children, 0)
      }));
    }
    return renderItems(children);
  };
  var renderMenu = function renderMenu() {
    var menuComponent = jsx("div", {
      className: classes.wrenchMenu,
      ref: floatingRefs.setFloating,
      style: floatingStyles,
      children: jsx(Fade, {
        in: showState,
        onExited: closeMenu,
        speed: "fast",
        alwaysRenderContent: alwaysRenderContent,
        children: jsx("div", {
          className: classes.wrenchMenuBody,
          role: role,
          id: menuID,
          ref: innerMenuRef,
          children: renderItemContainer()
        })
      })
    });
    return handlePortal(menuComponent, renderTo);
  };
  var renderTrigger = function renderTrigger() {
    var componentName = getDisplayName(menuTrigger);
    var isButton = componentName === 'Button';
    var isInput = componentName === 'Input';
    var trigger = /*#__PURE__*/cloneElement(menuTrigger, __assign(__assign(__assign({
      'aria-haspopup': role
    }, showState && {
      'aria-owns': menuID
    }), isButton && {
      'aria-pressed': (showState !== null && showState !== void 0 ? showState : false) || showProp
    }), {
      onFocus: isInput ? handleOnClick : undefined,
      onClick: !isInput ? handleOnClick : undefined,
      onKeyDown: handleOnTriggerKeyDown,
      tabIndex: 0,
      ref: anchorRef ? undefined : floatingRefs.setReference
    }));
    return trigger;
  };
  return jsx(MenuContext.Provider, {
    value: {
      nestedActiveId: nestedActiveId,
      setNestedActiveId: setNestedActiveId,
      nestedActiveRef: nestedActiveRef,
      handleMenuClose: handleMenuClose
    },
    children: jsxs("span", __assign({
      className: classes.wrenchMenuContainer,
      onBlur: closeOnBlur ? handleAutoCloseBlur : undefined,
      ref: menuRef,
      tabIndex: -1
    }, safeRest(rest), {
      children: [renderTrigger(), renderMenu()]
    }))
  });
};
Menu.Item = MenuItem;
Menu.Group = MenuGroup;
Menu.Header = MenuHeader;
Menu.Body = MenuBody;
Menu.Footer = MenuFooter;
Menu.Nested = MenuNested;

export { FOCUS_STATES, MENU_PLACEMENTS, Menu };
